import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../api';
import { navigationItems, dataFactoryContent } from '../content/data-factory-content';
import {
  VisionSection,
  DataHealthSection,
  FilesystemSection,
  RoadmapSection
} from '../components/data-factory';
import '../styles/data-factory.css';

/**
 * Data Factory Page
 *
 * Admin-only dashboard for data strategy and health monitoring.
 * Serves dual purpose: CEO presentation + interactive prototype.
 */
const DataFactory = () => {
  const [activeSection, setActiveSection] = useState('vision');
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const sectionRefs = useRef({});

  // Set up refs for each section
  const setSectionRef = useCallback((id) => (el) => {
    sectionRefs.current[id] = el;
  }, []);

  // Fetch data health metrics
  useEffect(() => {
    const fetchDataHealth = async () => {
      try {
        setLoading(true);
        const response = await apiFetch('/api/admin/data-health');
        if (response.ok) {
          const data = await response.json();
          setHealthData(data);
        } else {
          // For now, use mock data if API not yet implemented
          setHealthData(getMockHealthData());
        }
      } catch (err) {
        console.warn('Data health API not available, using mock data');
        setHealthData(getMockHealthData());
      } finally {
        setLoading(false);
      }
    };

    fetchDataHealth();
  }, []);

  // Intersection Observer for scroll-spy
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -50% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [loading]);

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsNavOpen(false);
    }
  };

  // Handle keyboard navigation
  const handleNavKeyDown = (e, sectionId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      scrollToSection(sectionId);
    }
  };

  // Close nav on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isNavOpen) {
        setIsNavOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isNavOpen]);

  return (
    <div className="data-factory">
      {/* Fixed Navigation Sidebar */}
      <nav className={`df-nav ${isNavOpen ? 'df-nav--open' : ''}`} aria-label="Page navigation">
        <h2 className="df-nav__title">Data Factory</h2>
        <ul className="df-nav__list">
          {navigationItems.map((item) => (
            <li key={item.id} className="df-nav__item">
              <button
                className={`df-nav__link ${activeSection === item.id ? 'df-nav__link--active' : ''}`}
                onClick={() => scrollToSection(item.id)}
                onKeyDown={(e) => handleNavKeyDown(e, item.id)}
                aria-current={activeSection === item.id ? 'true' : undefined}
              >
                <NavIcon name={item.icon} className="df-nav__icon" />
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Mobile Nav Toggle */}
      <button
        className="df-nav__toggle"
        onClick={() => setIsNavOpen(!isNavOpen)}
        aria-label="Toggle navigation"
        aria-expanded={isNavOpen}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {isNavOpen ? (
            <path d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Mobile Overlay */}
      {isNavOpen && (
        <div
          className="df-nav__overlay df-nav__overlay--visible"
          onClick={() => setIsNavOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main Content */}
      <div className="df-content">
        {/* Hero Section */}
        <header className="df-hero">
          <h1 className="df-hero__title">{dataFactoryContent.hero.title}</h1>
          <p className="df-hero__subtitle">{dataFactoryContent.hero.subtitle}</p>
          <p className="df-hero__tagline">{dataFactoryContent.hero.tagline}</p>
        </header>

        {/* Vision Section */}
        <section
          id="vision"
          ref={setSectionRef('vision')}
          className="df-section"
        >
          <VisionSection content={dataFactoryContent.vision} />
        </section>

        {/* Data Health Section */}
        <section
          id="health"
          ref={setSectionRef('health')}
          className="df-section"
        >
          <DataHealthSection
            content={dataFactoryContent.health}
            healthData={healthData}
            loading={loading}
          />
        </section>

        {/* Architecture Section */}
        <section
          id="architecture"
          ref={setSectionRef('architecture')}
          className="df-section"
        >
          <FilesystemSection content={dataFactoryContent.architecture} />
        </section>

        {/* Roadmap Section */}
        <section
          id="roadmap"
          ref={setSectionRef('roadmap')}
          className="df-section"
        >
          <RoadmapSection content={dataFactoryContent.roadmap} />
        </section>
      </div>
    </div>
  );
};

/**
 * Navigation Icon Component
 */
const NavIcon = ({ name, className }) => {
  const icons = {
    eye: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
    activity: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    folder: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      </svg>
    ),
    map: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
        <line x1="8" y1="2" x2="8" y2="18" />
        <line x1="16" y1="6" x2="16" y2="22" />
      </svg>
    )
  };

  return icons[name] || null;
};

/**
 * Mock health data for development/demo
 */
const getMockHealthData = () => ({
  transcripts: {
    total: 156,
    linked: 98,
    unlinked: 58,
    summarized: 87
  },
  students: {
    total: 45,
    with_data: 32,
    without_data: 13,
    coverage: generateMockCoverage(45)
  },
  tutors: {
    active: 12,
    activity: [
      { user_identifier: 'tutor1@lrbrainsolutions.com', transcript_count: 24 },
      { user_identifier: 'tutor2@lrbrainsolutions.com', transcript_count: 18 },
      { user_identifier: 'tutor3@lrbrainsolutions.com', transcript_count: 15 }
    ]
  },
  filesystem: {
    session_count: 42
  },
  gaps: [
    {
      id: 'drive-transcripts',
      severity: 'critical',
      title: 'Uncollected Google Drive Transcripts',
      estimated_data: '60-80% of session data'
    },
    {
      id: 'unlinked-sessions',
      severity: 'high',
      title: 'Unlinked Session Data',
      estimated_data: '37% of transcripts'
    }
  ]
});

/**
 * Generate mock coverage data for visualization
 */
const generateMockCoverage = (count) => {
  const names = ['Alex', 'Sam', 'Jordan', 'Taylor', 'Morgan', 'Casey', 'Riley', 'Quinn', 'Drew', 'Avery'];
  return Array.from({ length: count }, (_, i) => ({
    uuid: `student-${i}`,
    first_name: names[i % names.length],
    transcript_count: Math.floor(Math.random() * 10)
  }));
};

export default DataFactory;
